import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Alert, Box, Button, CircularProgress, Stack, Typography} from 'views/components';
import {AutoAwesome as GenerateIcon} from 'views/icons';
import {Translate, translate} from 'views/module';

import ClientJobCard from './ClientJobCard';
import GenerateJobDialog from './GenerateJobDialog';
import JobEditDialog from './JobEditDialog';

import backend from 'utils/backend';
import {toClientJob, toJobUpdateBody} from 'utils/backend/loom/job/job_adapter';

const HANDLED_ERRORS = [
  'CLT_001', 'JOB_001', 'JOB_003', 'JOB_006', 'JOB_007', 'JOB_008', 'JOB_009', 'JOB_010',
];

const errorMessage = (error) => {
  return translate(`job.ClientJobs.Errors.${HANDLED_ERRORS.includes(error.code) ? error.code : 'Default'}`);
};

const ClientJobs = (props) => {
  const {clientId} = props;
  const progressWrap = props.statusProgressWrap;

  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);
  const [dialog, setDialog] = React.useState(null);
  const [notes, setNotes] = React.useState([]);

  // Only a new client shows the spinner; a refresh keeps the cards, so the page does not jump to the top.
  React.useEffect(() => {
    setLoading(true);
  }, [clientId]);

  React.useEffect(() => {
    let isCancelled = false;

    const loadJobs = async () => {
      setError(false);

      try {
        const result = await backend.loom.job.listClientJobs(clientId);
        if (!isCancelled) setJobs(result.jobs.map(toClientJob));
      } catch (loadError) {
        console.error(loadError);
        if (isCancelled) return;
        setJobs([]);
        setError(true);
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    loadJobs();

    return () => {
      isCancelled = true;
    };
  }, [clientId, refresh]);

  const reload = React.useCallback(() => setRefresh((current) => current + 1), []);

  const closeDialog = React.useCallback(() => setDialog(null), []);

  const openRegenerate = React.useCallback((job) => setDialog({type: 'GENERATE', job}), []);

  const openEdit = React.useCallback((job) => setDialog({type: 'EDIT', job}), []);

  const generate = (guidance) => {
    return progressWrap(async () => {
      setNotes([]);
      const result = await backend.loom.job.generateJob(clientId, {
        guidance: guidance.trim() || null,
        jobId: dialog.job?.jobId,
      });
      setNotes(result.missingInformation);
      closeDialog();
      reload();
    }, {
      onErrorMessage: errorMessage,
      debugString: 'CLIENT_JOB_GENERATE',
    });
  };

  const saveJob = (form) => {
    return progressWrap(async () => {
      await backend.loom.job.updateJob(dialog.job.jobId, toJobUpdateBody(form));
      closeDialog();
      reload();
    }, {
      onErrorMessage: errorMessage,
      debugString: 'CLIENT_JOB_SAVE',
    });
  };

  const changeStatus = React.useCallback((job, status) => {
    return progressWrap(async () => {
      await backend.loom.job.updateJobStatus(job.jobId, {status});
      reload();
    }, {
      onErrorMessage: errorMessage,
      debugString: 'CLIENT_JOB_STATUS',
    });
  }, [reload]);

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{p: 2}}>
      <Stack direction='row' sx={{justifyContent: 'flex-end'}}>
        <Button startIcon={<GenerateIcon />} onClick={() => setDialog({type: 'GENERATE', job: null})}>
          <Translate expression='job.ClientJobs.Generate' />
        </Button>
      </Stack>
      {
        notes.length > 0 &&
        <Alert severity='warning' onClose={() => setNotes([])}>
          <Typography sx={{fontSize: '14px', fontWeight: 600}}>
            <Translate expression='job.ClientJobs.NotesTitle' />
          </Typography>
          <Box component='ul' sx={{m: 0, pl: 2}}>
            {
              notes.map((note) => (
                <li key={note}>{note}</li>
              ))
            }
          </Box>
        </Alert>
      }
      {
        error &&
        <Alert severity='error'>{translate('job.ClientJobs.LoadError')}</Alert>
      }
      {
        !error && jobs.length === 0 &&
        <Alert severity='info'>{translate('job.ClientJobs.Empty')}</Alert>
      }
      {
        jobs.map((job) => (
          <ClientJobCard
            key={job.jobId}
            job={job}
            onEdit={openEdit}
            onRegenerate={openRegenerate}
            onStatusChange={changeStatus}
          />
        ))
      }
      {
        dialog?.type === 'GENERATE' &&
        <GenerateJobDialog
          regenerate={Boolean(dialog.job)}
          onClose={closeDialog}
          onGenerate={generate}
        />
      }
      {
        dialog?.type === 'EDIT' &&
        <JobEditDialog
          job={dialog.job}
          onClose={closeDialog}
          onSave={saveJob}
        />
      }
    </Stack>
  );
};

ClientJobs.propTypes = {
  clientId: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientJobs));
